import { useState } from 'react';
import { invitationAPI } from '../services/api';
import './AddRelativeModal.css'; // Reuse modal styles

function InviteModal({ member, onClose }) {
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const memberName = `${member.first_name} ${member.last_name || ''}`.trim();

  const handleCreateInvite = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await invitationAPI.create(member.id, expiresInDays);
      setInvitation(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create invitation');
    } finally {
      setLoading(false);
    }
  };

  const getInviteUrl = () => {
    if (!invitation) return '';
    return `${window.location.origin}/register?invite=${invitation.code}`;
  };

  const handleCopy = async () => {
    const url = getInviteUrl();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Invite to Claim Profile</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <p className="modal-subtitle">
          Invite someone to claim <strong>{memberName}</strong>'s profile.
          They'll become the admin of this family member.
        </p>

        {error && <div className="modal-error">{error}</div>}

        {!invitation ? (
          <>
            <div className="form-group">
              <label htmlFor="expiresInDays">Invitation expires in</label>
              <select
                id="expiresInDays"
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(parseInt(e.target.value, 10))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--border-primary)',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                }}
              >
                <option value={1}>1 day</option>
                <option value={3}>3 days</option>
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
              </select>
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateInvite}
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Invitation'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="form-group">
              <label>Invitation Link</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={getInviteUrl()}
                  readOnly
                  style={{ flex: 1 }}
                />
                <button
                  className="btn btn-primary"
                  onClick={handleCopy}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            <div style={{
              padding: '12px',
              background: 'var(--bg-tertiary)',
              borderRadius: '6px',
              fontSize: '0.9rem',
              color: 'var(--text-secondary)',
            }}>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Expires:</strong> {new Date(invitation.expires_at).toLocaleDateString()}
              </p>
              <p style={{ margin: 0 }}>
                Share this link with {memberName}. When they register using this link,
                they'll claim this profile and become its admin.
              </p>
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default InviteModal;
